import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useCustomSidePadding } from '../config';
import { useDeviceType } from '../hooks/useDeviceType';
import { media } from './common-data';

export const MainContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["backgroundColor"].includes(prop),
}) <{
  backgroundColor?: string;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center; /* 아이템을 수평으로 중앙 정렬 */
  align-items: center; /* 아이템을 수직으로 중앙 정렬 */
  background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'}; /* backgroundColor 값이 있으면 그 값을 사용하고, 없으면 투명색 */

  ${media.mobile`
    // background-color: red;
  `}

  ${media.tablet`
    // background-color: blue;
  `}

  ${media.desktop`
    // background-color: lightgreen;
  `}
`;


export const AuthBox = styled.div<{
  backgroundColor?: string; // 배경색을 받을 수 있는 prop 추가
}>`
  background-color: ${({ backgroundColor }) => backgroundColor || 'white'}; /* backgroundColor 값이 있으면 그 값을 사용하고, 없으면 white */
  margin: 20px;
  border-radius: 8px; /* 모서리 둥글게 */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* 그림자 추가 */
  width: 100%;
`;


export const VerticalLayout = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "backgroundColor", "hoverBackgroundColor", "wrapContents"].includes(prop),
}) <{
  width?: string | number;
  height?: string | number;
  paddingTop?: string | number;
  paddingRight?: string | number;
  paddingBottom?: string | number;
  paddingLeft?: string | number;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  wrapContents?: boolean;  // wrapContents prop 추가
}>`
  display: flex;
  flex-direction: column;  /* 세로 방향으로 배치 */
  align-items: start;
  justify-content: flex-start;
  text-align: start;
  box-sizing: border-box;
  width: ${({ width }) => (width ? (typeof width === 'number' ? `${width}px` : width) : '100%')};
  height: ${({ wrapContents, height }) => wrapContents ? 'auto' : (height ? (typeof height === 'number' ? `${height}px` : height) : '100%')};  /* wrapContents가 true일 때 height: auto */
  padding-top: ${({ paddingTop }) => (paddingTop ? (typeof paddingTop === 'number' ? `${paddingTop}px` : paddingTop) : '0')};
  padding-right: ${({ paddingRight }) => (paddingRight ? (typeof paddingRight === 'number' ? `${paddingRight}px` : paddingRight) : '0')};
  padding-bottom: ${({ paddingBottom }) => (paddingBottom ? (typeof paddingBottom === 'number' ? `${paddingBottom}px` : paddingBottom) : '0')};
  padding-left: ${({ paddingLeft }) => (paddingLeft ? (typeof paddingLeft === 'number' ? `${paddingLeft}px` : paddingLeft) : '0')};
  background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'};

  &:hover {
    background-color: ${({ hoverBackgroundColor, backgroundColor }) =>
    hoverBackgroundColor || backgroundColor || "transparent"};
  }
`;

export const HoverVerticalLayout = styled(VerticalLayout)`
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #EEEEF1;
  }
`;

export const HorizontalLayout = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "backgroundColor", "wrapContents"].includes(prop),
}) <{
  width?: string | number;
  height?: string | number;
  paddingTop?: string | number;
  paddingRight?: string | number;
  paddingBottom?: string | number;
  paddingLeft?: string | number;
  backgroundColor?: string;
  wrapContents?: boolean;  // wrapContents prop 추가
}>`
  display: flex;
  flex-direction: row; /* 수평 레이아웃 */
  align-items: start;
  justify-content: flex-start; /* 아이템을 수평으로 시작 부분에 정렬 */
  text-align: start;
  box-sizing: border-box;
  width: ${({ wrapContents, width }) => wrapContents ? 'auto' : (width ? (typeof width === 'number' ? `${width}px` : width) : '100%')};  /* wrapContents가 true일 때 width: auto */
  height: ${({ height }) => (height ? (typeof height === 'number' ? `${height}px` : height) : '100%')};
  padding-top: ${({ paddingTop }) => (paddingTop ? (typeof paddingTop === 'number' ? `${paddingTop}px` : paddingTop) : '0')};
  padding-right: ${({ paddingRight }) => (paddingRight ? (typeof paddingRight === 'number' ? `${paddingRight}px` : paddingRight) : '0')};
  padding-bottom: ${({ paddingBottom }) => (paddingBottom ? (typeof paddingBottom === 'number' ? `${paddingBottom}px` : paddingBottom) : '0')};
  padding-left: ${({ paddingLeft }) => (paddingLeft ? (typeof paddingLeft === 'number' ? `${paddingLeft}px` : paddingLeft) : '0')};
  background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'};
`;

export const HoverHorizontalLayout = styled(HorizontalLayout)`
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #EEEEF1;
  }
`;

export const FrameLayout = styled.div<{
  width?: string | number;  // width 값은 string 또는 number로 받을 수 있도록 설정
  height?: string | number; // height 값도 string 또는 number로 받을 수 있도록 설정
  backgroundColor?: string; // 배경색을 받을 수 있는 prop 추가
}>`
  position: relative; /* 자식 요소를 절대 위치로 배치할 수 있도록 설정 */
  width: ${({ width }) => (width ? (typeof width === 'number' ? `${width}px` : width) : '100%')}; /* width 값이 있으면 그 값을 사용하고, 없으면 100% */
  height: ${({ height }) => (height ? (typeof height === 'number' ? `${height}px` : height) : '100%')}; /* height 값이 있으면 그 값을 사용하고, 없으면 100% */
  background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'}; /* backgroundColor 값이 있으면 그 값을 사용하고, 없으면 투명색 */
`;

export const FrameChild = styled.div<{
  top?: string | number;      // top 값은 string 또는 number로 받을 수 있도록 설정
  left?: string | number;     // left 값도 string 또는 number로 받을 수 있도록 설정
  right?: string | number;    // right 값도 string 또는 number로 받을 수 있도록 설정
  bottom?: string | number;   // bottom 값도 string 또는 number로 받을 수 있도록 설정
  width?: string | number;    // width 값은 string 또는 number로 받을 수 있도록 설정
  height?: string | number;   // height 값도 string 또는 number로 받을 수 있도록 설정
  backgroundColor?: string;   // 배경색을 받을 수 있는 prop 추가
}>`
    position: absolute; /* 부모(FrameLayout)를 기준으로 위치 지정 */
    top: ${({ top }) => (top !== undefined ? (typeof top === 'number' ? `${top}px` : top) : 'auto')};
    left: ${({ left }) => (left !== undefined ? (typeof left === 'number' ? `${left}px` : left) : 'auto')};
    right: ${({ right }) => (right !== undefined ? (typeof right === 'number' ? `${right}px` : right) : 'auto')};
    bottom: ${({ bottom }) => (bottom !== undefined ? (typeof bottom === 'number' ? `${bottom}px` : bottom) : 'auto')};
    width: ${({ width }) => (width !== undefined ? (typeof width === 'number' ? `${width}px` : width) : 'auto')};
    height: ${({ height }) => (height !== undefined ? (typeof height === 'number' ? `${height}px` : height) : 'auto')};
    background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'}; /* backgroundColor 값이 있으면 그 값을 사용하고, 없으면 투명색 */
  `;

export const CenterLayout = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "backgroundColor", "wrapContents"].includes(prop),
}) <{
  width?: string | number;
  height?: string | number;
  paddingTop?: string | number;
  paddingRight?: string | number;
  paddingBottom?: string | number;
  paddingLeft?: string | number;
  backgroundColor?: string;
  wrapContents?: boolean;
}>`
    display: flex;
    flex-direction: row;
    justify-content: center;      /* 수평 가운데 정렬 */
    align-items: center;          /* 수직 가운데 정렬 */
    text-align: center;
    box-sizing: border-box;
  
    width: ${({ wrapContents, width }) =>
    wrapContents ? 'auto' : (width ? (typeof width === 'number' ? `${width}px` : width) : '100%')};
  
    height: ${({ height }) =>
    height ? (typeof height === 'number' ? `${height}px` : height) : '100%'};
  
    padding-top: ${({ paddingTop }) =>
    paddingTop ? (typeof paddingTop === 'number' ? `${paddingTop}px` : paddingTop) : '0'};
  
    padding-right: ${({ paddingRight }) =>
    paddingRight ? (typeof paddingRight === 'number' ? `${paddingRight}px` : paddingRight) : '0'};
  
    padding-bottom: ${({ paddingBottom }) =>
    paddingBottom ? (typeof paddingBottom === 'number' ? `${paddingBottom}px` : paddingBottom) : '0'};
  
    padding-left: ${({ paddingLeft }) =>
    paddingLeft ? (typeof paddingLeft === 'number' ? `${paddingLeft}px` : paddingLeft) : '0'};
  
    background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'};
  `;

export const HorizontalEqualLayout = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["gap", "backgroundColor", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft"].includes(prop),
}) <{
  width?: string | number;
  height?: string | number;
  gap?: string | number;
  backgroundColor?: string;
  paddingTop?: string | number;
  paddingRight?: string | number;
  paddingBottom?: string | number;
  paddingLeft?: string | number;
}>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
  
    width: ${({ width }) =>
    width ? (typeof width === 'number' ? `${width}px` : width) : '100%'};
      
    height: ${({ height }) =>
    height ? (typeof height === 'number' ? `${height}px` : height) : 'auto'};
  
    background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'};
  
    padding-top: ${({ paddingTop }) =>
    paddingTop ? (typeof paddingTop === 'number' ? `${paddingTop}px` : paddingTop) : '0'};
  
    padding-right: ${({ paddingRight }) =>
    paddingRight ? (typeof paddingRight === 'number' ? `${paddingRight}px` : paddingRight) : '0'};
  
    padding-bottom: ${({ paddingBottom }) =>
    paddingBottom ? (typeof paddingBottom === 'number' ? `${paddingBottom}px` : paddingBottom) : '0'};
  
    padding-left: ${({ paddingLeft }) =>
    paddingLeft ? (typeof paddingLeft === 'number' ? `${paddingLeft}px` : paddingLeft) : '0'};
  
    gap: ${({ gap }) =>
    gap ? (typeof gap === 'number' ? `${gap}px` : gap) : '0'};
  
    & > * {
      flex: 1; // ✅ 자식 요소가 동일한 너비로 분배됨
    }
  `;

export const VerticalEqualLayout = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["gap", "backgroundColor", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft"].includes(prop),
}) <{
  width?: string | number;
  height?: string | number;
  gap?: string | number;
  backgroundColor?: string;
  paddingTop?: string | number;
  paddingRight?: string | number;
  paddingBottom?: string | number;
  paddingLeft?: string | number;
}>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
  
    width: ${({ width }) =>
    width ? (typeof width === 'number' ? `${width}px` : width) : '100%'};
  
    height: ${({ height }) =>
    height ? (typeof height === 'number' ? `${height}px` : height) : 'auto'};
  
    background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'};
  
    padding-top: ${({ paddingTop }) =>
    paddingTop ? (typeof paddingTop === 'number' ? `${paddingTop}px` : paddingTop) : '0'};
  
    padding-right: ${({ paddingRight }) =>
    paddingRight ? (typeof paddingRight === 'number' ? `${paddingRight}px` : paddingRight) : '0'};
  
    padding-bottom: ${({ paddingBottom }) =>
    paddingBottom ? (typeof paddingBottom === 'number' ? `${paddingBottom}px` : paddingBottom) : '0'};
  
    padding-left: ${({ paddingLeft }) =>
    paddingLeft ? (typeof paddingLeft === 'number' ? `${paddingLeft}px` : paddingLeft) : '0'};
  
    gap: ${({ gap }) =>
    gap ? (typeof gap === 'number' ? `${gap}px` : gap) : '0'};
  
    & > * {
      flex: 1; // ✅ 자식 요소가 동일한 높이로 분배됨
    }
  `;

export const VerticalBorderBox = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #B2B2B2;
  border-radius: 8px;
  box-sizing: border-box;
`;

type PaddingMode = 'fixed' | 'auto';

interface ResponsivePaddingOptions {
  /** 'fixed' = 기기별 고정 패딩 / 'auto' = (window.innerWidth - width) / 2 */
  mode?: PaddingMode;              // default: 'auto'
  /** auto 모드에서 최소 좌우 패딩(px) */
  minHorizontal?: number;          // default: 16
  /** auto 모드에서 콘텐츠 폭 강제 지정 */
  contentWidthOverride?: number;   // optional
}

export const responsivePaddingConfig = {
  // 'fixed'  : 기기별 고정 패딩 사용 (현재 방식)
  // 'auto'   : (window.innerWidth - width) / 2 로 자동 계산
  mode: 'auto' as 'fixed' | 'auto',

  // auto 모드에서 최소 패딩 보장 (선택)
  minHorizontal: 16,
};

export const useResponsivePadding = (options: ResponsivePaddingOptions = {}) => {
  const {
    mode,
    minHorizontal = 16,
    contentWidthOverride
  } = options;

  // 🔑 config와 props로 최종 모드 결정
  const finalMode: PaddingMode = mode
    ? mode
    : (useCustomSidePadding ? 'auto' : 'fixed');

  const { isMobile, isTablet, isDesktop, isPad, screenWidth } = useDeviceType();

  const base = useMemo(() => {
    if (screenWidth <= 707) {
      return { horizontal: 16, top: isMobile ? 100 : isTablet ? 200 : 240, topSectionBottom: isMobile ? 50 : isTablet ? 80 : 120, width: (360 - (16 * 2)) };
    } else if (screenWidth <= 1119) {
      return { horizontal: 30, top: isMobile ? 100 : isTablet ? 200 : 240, topSectionBottom: isMobile ? 50 : isTablet ? 80 : 120, width: (768 - (30 * 2)) };
    } else if (screenWidth <= 1439) {
      return { horizontal: 40, top: isMobile ? 100 : isTablet ? 200 : 240, topSectionBottom: isMobile ? 50 : isTablet ? 80 : 120, width: (1200 - (40 * 2)) };
    } else if (screenWidth <= 1919) {
      return { horizontal: 80, top: isMobile ? 100 : isTablet ? 200 : 240, topSectionBottom: isMobile ? 50 : isTablet ? 80 : 120, width: (1440 - (80 * 2)) };
    }
    return { horizontal: 180, top: 240, topSectionBottom: 120, width: (1920 - (180 * 2)) };
  }, [isMobile, isTablet, isDesktop, screenWidth]);

  const contentWidth = contentWidthOverride ?? base.width;

  const [autoHorizontal, setAutoHorizontal] = useState(base.horizontal);

  useEffect(() => {
    if (finalMode !== 'auto') {
      setAutoHorizontal(base.horizontal);
      return;
    }

    const calc = () => {
      if (typeof window === 'undefined') {
        setAutoHorizontal(base.horizontal);
        return;
      }
      const screenWidth = window.innerWidth;
      const raw = (screenWidth - contentWidth) / 2;
      setAutoHorizontal(Math.max(raw, minHorizontal, 0));
    };

    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [finalMode, base.horizontal, contentWidth, minHorizontal]);

  // 최종 콘텐츠 폭
  const computedWidth =
    typeof window !== 'undefined'
      ? Math.min(window.innerWidth - 2 * base.horizontal, 1560) // 콘텐츠 폭 제한
      : contentWidth;

  // 남는 공간 기반으로 horizontal 재계산
  const horizontal =
    typeof window !== 'undefined'
      ? Math.max((window.innerWidth - computedWidth) / 2, minHorizontal)
      : base.horizontal;

  return {
    horizontal,
    top: base.top,
    topSectionBottom: base.topSectionBottom,
    width: computedWidth,
    modeUsed: finalMode,
  };
};
